/* BUDDY AVANZA — Landing pública (/buddy-avanza) · LIGHT THEME
   Sólo visual. NO toca backend, checkout logic, N1CO ni consent.
   Datos comerciales vía GET /api/avanzo-b2c/offer-config.
   Excepción de contraste: Buddy differentiator section usa fondo oscuro
   para destacar como diferenciador (ritmo visual).
*/
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Sparkles, Target, MessageCircle, LineChart,
  ClipboardCheck, Trophy, ShieldCheck, Zap, Users2, Check,
  BookOpen, Atom, Feather, Landmark, Play,
} from "lucide-react";
import BuddyAvanzaLegalFooter from "@/components/BuddyAvanzaLegalFooter";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCESS_LABEL = "2 meses";

/* ── Áreas (identidad visual por materia) ─────────────────────── */
const SUBJECTS = [
  { key: "math",    label: "Precálculo",           color: "#419cca",
    accent: "from-[#419cca] to-[#2f7dab]", icon: BookOpen,
    micro: "Álgebra, funciones y modelado paso a paso.", pct: 72 },
  { key: "lang",    label: "Lenguaje y Literatura", color: "#8b5cf6",
    accent: "from-[#8b5cf6] to-[#6d28d9]", icon: Feather,
    micro: "Comprensión lectora, redacción y análisis.", pct: 81 },
  { key: "science", label: "Ciencia y Tecnología",  color: "#10b981",
    accent: "from-[#10b981] to-[#047857]", icon: Atom,
    micro: "Biología, física y razonamiento científico.", pct: 64 },
  { key: "civic",   label: "Ciudadanía y Valores",  color: "#f59e0b",
    accent: "from-[#f59e0b] to-[#b45309]", icon: Landmark,
    micro: "Historia, ética y realidad salvadoreña.", pct: 76 },
];

const JOURNEY = [
  { icon: Target,          title: "Diagnóstico",       copy: "Descubre tu punto de partida en cada área." },
  { icon: Sparkles,        title: "Entrenamiento",     copy: "Práctica personalizada según lo que necesitas reforzar." },
  { icon: MessageCircle,   title: "Buddy te acompaña", copy: "Cuando te atoras, Buddy te ayuda a pensar el problema." },
  { icon: LineChart,       title: "Progreso visible",  copy: "Ves tu avance semana a semana en las 4 áreas." },
  { icon: ClipboardCheck,  title: "Control de progreso", copy: "Mini-simulacros para medir cómo vas." },
  { icon: Zap,             title: "Simulacro 1",       copy: "Un ensayo real de la prueba, con retroalimentación." },
  { icon: ShieldCheck,     title: "Refuerzo final",    copy: "Recuperación de lo que aún necesitas practicar." },
  { icon: Trophy,          title: "Simulacro Final",   copy: "Llegas al día de la prueba con confianza." },
];


export default function BuddyAvanzaLanding() {
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/avanzo-b2c/offer-config`)
      .then((r) => setOffer(r.data))
      .catch(() => setOffer(null));
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = ""; };
  }, []);

  if (!offer) {
    return (
      <div className="min-h-screen bg-white text-slate-600 flex items-center justify-center">
        Cargando…
      </div>
    );
  }

  const price = Number(offer.offer_price).toFixed(2);
  const dailyDisplay = offer.daily_equivalent_display;

  return (
    <div data-testid="buddy-avanza-landing" className="bg-white text-slate-900 antialiased overflow-x-hidden">
      <StickyNav price={price} />
      <Hero offer={offer} />
      <Subjects />
      <Journey />
      <BuddySection />
      <ProgressPreview />
      <SimulacrosSection />
      <Pricing offer={offer} price={price} dailyDisplay={dailyDisplay} />
      <FamilySection />
      <Footer />
      <BuddyAvanzaLegalFooter />
      <AmbientBackground />
      <MobileStickyCTA price={price} />

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform: translateY(20px);} to { opacity:1; transform: none;} }
        @keyframes floaty { 0%,100% { transform: translateY(0);} 50% { transform: translateY(-8px);} }
        .fade-up { animation: fadeUp .7s cubic-bezier(.2,.7,.3,1) both; }
        .fade-up-d1 { animation-delay: .08s; }
        .fade-up-d2 { animation-delay: .16s; }
        .fade-up-d3 { animation-delay: .24s; }
        .fade-up-d4 { animation-delay: .32s; }
        .float-slow { animation: floaty 6s ease-in-out infinite; }
        .mesh-glow-light {
          background:
            radial-gradient(60% 40% at 15% 10%, rgba(65,156,202,.16), transparent 60%),
            radial-gradient(45% 35% at 90% 20%, rgba(139,92,246,.16), transparent 60%),
            radial-gradient(70% 60% at 60% 90%, rgba(16,185,129,.08), transparent 60%);
        }
      `}</style>
    </div>
  );
}


/* ═══════════════════════ STICKY NAV ═══════════════════════ */
function StickyNav({ price }) {
  return (
    <div className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" className="flex items-center" data-testid="landing-back-home">
          <img src="/buddy-avanza-icon.png" alt="BUDDY AVANZA" className="h-14 w-14 object-contain transition-transform hover:scale-105" />
        </Link>
        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-600">
          <span className="opacity-80">Pago único · {ACCESS_LABEL}</span>
          <span className="font-bold text-slate-900">US${price}</span>
        </div>
        <Link
          to="/avanzo/comprar"
          data-testid="nav-cta-buy"
          className="text-sm font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 px-4 py-2 rounded-full transition-all hover:scale-[1.03] active:scale-95 shadow-md shadow-[#8b5cf6]/25"
        >
          Comenzar
        </Link>
      </div>
    </div>
  );
}


/* ═══════════════════════ HERO ═══════════════════════ */
function Hero({ offer }) {
  const price = Number(offer.offer_price).toFixed(2);
  const dailyDisplay = offer.daily_equivalent_display;
  return (
    <header className="relative overflow-hidden mesh-glow-light" data-testid="hero-section">
      <div className="max-w-6xl mx-auto px-4 pt-12 md:pt-16 pb-8 md:pb-16 relative z-10">
        <div className="fade-up flex justify-center" data-testid="hero-logo">
          <img src="/buddy-avanza-logo-transparent.png" alt="BUDDY AVANZA"
               className="w-64 h-64 sm:w-80 sm:h-80 md:w-[26rem] md:h-[26rem] object-contain drop-shadow-[0_20px_50px_rgba(139,92,246,0.25)]" />
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 pb-24 md:pb-32 grid md:grid-cols-12 gap-10 items-center relative z-10">
        <div className="md:col-span-7">
          <h1 className="fade-up fade-up-d1 text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-[1.05] text-slate-900"
               data-testid="hero-title">
            Prepárate. Practica.
            <span className="block bg-gradient-to-r from-[#419cca] via-[#6d5cf5] to-[#10b981] bg-clip-text text-transparent">
              Avanza.
            </span>
          </h1>
          <p className="fade-up fade-up-d2 mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed"
              data-testid="hero-subheadline">
            Entrena para la Prueba AVANZO con práctica personalizada, simulacros
            reales y acompañamiento de Buddy en cada tema.
          </p>

          <div className="fade-up fade-up-d3 mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/avanzo/comprar"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center px-6 py-4 rounded-full text-base font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 transition-all shadow-xl shadow-[#8b5cf6]/25 hover:scale-[1.02] active:scale-95"
            >
              Comenzar mi preparación
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#como-funciona"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center px-6 py-4 rounded-full text-base font-semibold text-slate-800 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
            >
              <Play className="w-4 h-4 mr-2" /> Ver cómo funciona
            </a>
          </div>

          <div className="fade-up fade-up-d4 mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-700">
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 4 áreas de la prueba</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Buddy Tutor incluido</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Simulaciones AVANZO</span>
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="fade-up fade-up-d3 relative rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-[#8b5cf6]/10 ring-1 ring-slate-900/[0.02]"
                data-testid="hero-price-card">
            <div className="absolute -top-5 left-6 inline-flex items-center gap-2 text-sm sm:text-base tracking-[3px] uppercase font-bold px-5 py-2 rounded-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white shadow-xl shadow-[#8b5cf6]/30 ring-2 ring-white"
                 data-testid="hero-offer-badge">
              <Sparkles className="w-4 h-4" /> Oferta especial
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-black text-slate-900" data-testid="hero-price">
                US${price}
              </span>
              <span className="text-slate-500 text-sm">USD</span>
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-700"
                  data-testid="hero-modality">
              Pago único · {ACCESS_LABEL} de acceso
            </div>
            <div className="mt-3 space-y-1 text-xs">
              {dailyDisplay && (
                <div className="text-emerald-600 font-semibold" data-testid="hero-daily">
                  {dailyDisplay}
                </div>
              )}
              <div className="text-slate-500">Sin renovación automática</div>
              <div className="text-slate-500">Sin mensualidades</div>
            </div>
            <div className="mt-5 border-t border-slate-100 pt-4 grid grid-cols-4 gap-2 text-center text-[11px] text-slate-600">
              {SUBJECTS.map((s) => (
                <div key={s.key} className="flex flex-col items-center gap-1">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: s.color + "18", border: `1px solid ${s.color}44` }}>
                    <s.icon className="w-4 h-4" style={{ color: s.color }} />
                  </div>
                  <div className="leading-tight">{s.label.split(" ")[0]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}


/* ═══════════════════════ SUBJECTS ═══════════════════════ */
function Subjects() {
  return (
    <section id="areas" className="relative py-20 md:py-28 bg-slate-50/50" data-testid="subjects-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="subjects-title">
            Las 4 áreas de la Prueba AVANZO, cubiertas.
          </h2>
          <p className="mt-3 text-slate-600">
            Cada área tiene su propio banco de reactivos, retroalimentación y
            acompañamiento pedagógico.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SUBJECTS.map((s, i) => (
            <div key={s.key}
                  data-testid={`subject-card-${s.key}`}
                  className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-white p-5 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                  style={{ animationDelay: `${i * 60}ms` }}>
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-15 blur-2xl"
                    style={{ background: s.color }} />
              <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${s.accent} shadow-lg`}
                    style={{ boxShadow: `0 6px 18px -6px ${s.color}66` }}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="mt-4 font-semibold text-lg leading-tight text-slate-900">{s.label}</h3>
              <p className="mt-1 text-sm text-slate-600">{s.micro}</p>
              <div className="mt-4 flex items-center text-xs text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full mr-2" style={{ background: s.color }} />
                Banco pedagógico dedicado
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ═══════════════════════ JOURNEY ═══════════════════════ */
function Journey() {
  return (
    <section id="como-funciona" className="relative py-20 md:py-28 border-y border-slate-100 bg-white"
             data-testid="journey-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[3px] uppercase text-[#419cca] font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> Así te prepara BUDDY AVANZA
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="journey-title">
            Un recorrido diseñado para llegar preparado el día de la prueba.
          </h2>
        </div>
        <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="journey-steps">
          {JOURNEY.map((step, i) => (
            <li key={step.title}
                className="relative rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#419cca]/40 hover:shadow-lg transition-all"
                data-testid={`journey-step-${i + 1}`}>
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-400 tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#419cca]/12 to-[#8b5cf6]/12 border border-slate-200 flex items-center justify-center">
                  <step.icon className="w-4 h-4 text-[#419cca]" />
                </div>
              </div>
              <div className="mt-3 font-semibold text-slate-900">{step.title}</div>
              <div className="mt-1 text-sm text-slate-600 leading-relaxed">{step.copy}</div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}


/* ═══════════════════════ BUDDY DIFFERENTIATOR (dark island) ═══════════════════════ */
function BuddySection() {
  return (
    <section className="relative py-20 md:py-28 bg-gradient-to-br from-[#0b0f19] via-[#111827] to-[#0b0f19] text-slate-100 overflow-hidden"
              data-testid="buddy-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-80 h-80 rounded-full blur-3xl opacity-20" style={{ background: "#8b5cf6" }} />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl opacity-15" style={{ background: "#419cca" }} />
      </div>
      <div className="relative max-w-6xl mx-auto px-4 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[3px] uppercase text-fuchsia-300/90 font-semibold">
            <MessageCircle className="w-3.5 h-3.5" /> El diferenciador
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight" data-testid="buddy-title">
            ¿Te atascaste? <span className="bg-gradient-to-r from-[#a78bfa] to-[#f472b6] bg-clip-text text-transparent">Buddy te acompaña.</span>
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
            Buddy no te da simplemente la respuesta. Te ayuda a <b>analizar el problema</b>,
            encontrar una estrategia y avanzar paso a paso.
          </p>
          <p className="mt-3 text-slate-400 leading-relaxed max-w-xl">
            En Matemática Buddy analiza el problema contigo, propone una estrategia
            y te guía paso a paso — para que la próxima vez lo resuelvas solo.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { t: "No responde por ti", d: "Te enseña a pensar el problema." },
              { t: "Explica el porqué",  d: "Rationale al terminar cada práctica." },
              { t: "Guía por pasos",     d: "Especialmente fuerte en matemática." },
            ].map((it) => (
              <div key={it.t} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="text-sm font-semibold">{it.t}</div>
                <div className="mt-1 text-xs text-slate-400">{it.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-5">
          <BuddyMockChat />
        </div>
      </div>
    </section>
  );
}


function BuddyMockChat() {
  return (
    <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-5 backdrop-blur-xl shadow-2xl shadow-black/40"
          data-testid="buddy-mock-chat">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-sm font-semibold">Buddy</div>
          <div className="text-[11px] text-emerald-400">en línea</div>
        </div>
      </div>
      <div className="space-y-3 text-sm">
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/[0.06] px-3.5 py-2.5 text-slate-100">
          Vamos a resolver <b>2x + 3 = 11</b>. ¿Qué crees que debemos hacer primero?
        </div>
        <div className="max-w-[85%] ml-auto rounded-2xl rounded-tr-sm bg-cyan-500/20 border border-cyan-400/25 px-3.5 py-2.5 text-slate-100">
          ¿Dividir todo entre 2?
        </div>
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/[0.06] px-3.5 py-2.5 text-slate-100">
          Antes de dividir, primero <b>despejamos</b> el término con la variable.
          Réstale 3 a los dos lados.
        </div>
        <div className="text-[11px] text-slate-500 pl-1">Buddy escribiendo…</div>
      </div>
      <div className="mt-4 h-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center px-3 text-xs text-slate-500">
        Escribe tu razonamiento…
      </div>
    </div>
  );
}


/* ═══════════════════════ PROGRESS PREVIEW ═══════════════════════ */
function ProgressPreview() {
  return (
    <section className="relative py-20 md:py-28 bg-slate-50/50" data-testid="progress-preview-section">
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[3px] uppercase text-emerald-600 font-semibold">
            <LineChart className="w-3.5 h-3.5" /> Progreso visible
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            Sabes exactamente dónde estás.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Cada semana ves cómo evoluciona tu preparación en las 4 áreas.
            El estudiante sabe qué reforzar, y el padre acompaña sin adivinar.
          </p>
          <div className="mt-4 inline-block text-[10px] tracking-[2px] uppercase text-slate-500 border border-slate-200 bg-white rounded-full px-3 py-1">
            Vista previa · datos ilustrativos
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/[0.04]"
                data-testid="progress-preview-card">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-slate-900">Tu progreso</div>
              <div className="text-[11px] text-slate-500">Semana 4 · demo</div>
            </div>
            <div className="space-y-4" data-testid="progress-bars">
              {SUBJECTS.map((s) => (
                <ProgressRow key={s.key} label={s.label} pct={s.pct} color={s.color} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgressRow({ label, pct, color }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); io.disconnect(); }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700">{label}</span>
        <span className="font-semibold" style={{ color }}>{pct}%</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-[1200ms] ease-out"
              style={{
                width: visible ? `${pct}%` : "0%",
                background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                boxShadow: `0 0 12px ${color}55`,
              }} />
      </div>
    </div>
  );
}


/* ═══════════════════════ SIMULACROS ═══════════════════════ */
function SimulacrosSection() {
  const items = [
    { key: "pc",  title: "Control de progreso", desc: "Mini-simulacros para medir cómo vas.",  Icon: ClipboardCheck, color: "#419cca" },
    { key: "s1",  title: "Simulacro 1",         desc: "Un ensayo real con retroalimentación.", Icon: Zap,            color: "#8b5cf6" },
    { key: "rf",  title: "Refuerzo personalizado", desc: "Practicas lo que aún necesitas.",     Icon: Sparkles,       color: "#10b981" },
    { key: "sf",  title: "Simulacro Final",     desc: "Llegas al día de la prueba con confianza.", Icon: Trophy,      color: "#f59e0b" },
  ];
  return (
    <section className="relative py-20 md:py-28 bg-white" data-testid="simulacros-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="simulacros-title">
            Practica antes de enfrentarte a la prueba.
          </h2>
          <p className="mt-3 text-slate-600">
            No es solo estudiar. Es entrenar en condiciones parecidas al día real.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4 relative">
          {items.map((it, i) => (
            <div key={it.key} className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`simulacro-${it.key}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center border"
                    style={{ background: `${it.color}12`, borderColor: `${it.color}55` }}>
                <it.Icon className="w-5 h-5" style={{ color: it.color }} />
              </div>
              <div className="mt-4 text-xs font-bold tracking-widest uppercase" style={{ color: it.color }}>
                Etapa {i + 1}
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{it.title}</div>
              <div className="mt-1 text-sm text-slate-600">{it.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ═══════════════════════ PRICING (light premium) ═══════════════════════ */
function Pricing({ offer, price, dailyDisplay }) {
  const features = [
    "Las 4 áreas de la Prueba AVANZO",
    "Entrenamiento personalizado por materia",
    "Buddy Tutor incluido (paso a paso)",
    "Controles de progreso",
    "Simulacro 1 + Simulacro Final",
    "Seguimiento visible de tu avance",
    "Acceso familiar cuando el estudiante es menor",
  ];
  return (
    <section id="pricing" className="relative py-20 md:py-28 mesh-glow-light" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            Un solo pago. <span className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] bg-clip-text text-transparent">{ACCESS_LABEL} de preparación completa.</span>
          </h2>
          <p className="mt-4 text-slate-600">
            Sin mensualidades. Sin renovación automática. Sin sorpresas. Empiezas cuando estés listo.
          </p>
          <div className="mt-6 space-y-2">
            {features.map((f) => (
              <div key={f} className="flex items-start gap-3 text-slate-700 text-sm">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-600" />
                </div>
                {f}
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="relative rounded-3xl overflow-hidden bg-white p-8 shadow-2xl shadow-[#8b5cf6]/10 ring-1 ring-slate-900/[0.04] border border-slate-100"
                data-testid="pricing-card">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#419cca] via-[#8b5cf6] to-[#10b981]" />
            <div className="flex items-center gap-3 mb-2">
              <div className="text-[11px] tracking-[3px] uppercase text-[#8b5cf6] font-bold">
                BUDDY AVANZA
              </div>
              <div className="text-[10px] tracking-[2px] uppercase font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white">
                Más elegido
              </div>
            </div>
            <div className="text-slate-600 text-sm">{ACCESS_LABEL} de preparación</div>

            <div className="mt-4 flex items-end gap-3 flex-wrap">
              <div>
                <div className="text-6xl md:text-7xl font-black text-slate-900 leading-none tracking-tight"
                      data-testid="pricing-offer-price">
                  US${price}
                </div>
                {dailyDisplay && (
                  <div className="mt-2 text-sm text-emerald-600 font-semibold" data-testid="pricing-daily">
                    {dailyDisplay}
                  </div>
                )}
              </div>
              <div className="text-sm text-slate-500 pb-2">USD · pago único</div>
            </div>
            <div className="mt-1 text-sm text-slate-500">Sin mensualidades · Sin renovación automática</div>

            <Link to="/avanzo/comprar" data-testid="pricing-cta"
                   className="group mt-8 flex items-center justify-center px-6 py-4 rounded-full text-base font-bold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 transition-all shadow-xl shadow-[#8b5cf6]/25 hover:scale-[1.02] active:scale-95">
              Obtener {ACCESS_LABEL} de acceso
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <div className="mt-3 text-center text-[11px] text-slate-500">
              Pago único · Sin renovación automática · Pago seguro por N1CO
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ═══════════════════════ FAMILY ═══════════════════════ */
function FamilySection() {
  return (
    <section className="relative py-20 md:py-24 bg-slate-50/50" data-testid="family-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center shadow-lg shadow-slate-900/[0.03]">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[3px] uppercase text-[#419cca] font-semibold">
              <Users2 className="w-3.5 h-3.5" /> Para las familias
            </div>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold text-slate-900" data-testid="family-title">
              También puedes acompañar su progreso.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
              Cuando el estudiante es menor, activamos accesos independientes: el
              estudiante entra con su propio correo y contraseña, y el padre o
              responsable con los suyos. Cada uno ve lo que le corresponde y las
              credenciales <b>no se comparten</b>.
            </p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Acceso separado estudiante y adulto</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Consentimiento parental explícito</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> El padre puede ver el progreso</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Contraseñas independientes</li>
            </ul>
          </div>
          <div className="md:col-span-5">
            <FamilyAccountsMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function FamilyAccountsMock() {
  return (
    <div className="relative rounded-2xl border border-slate-200 bg-slate-50 p-5"
          data-testid="family-mock">
      {[
        { role: "Estudiante", email: "alumna@correo.sv", color: "#419cca" },
        { role: "Padre / Madre / Tutor", email: "familia@correo.sv", color: "#8b5cf6" },
      ].map((a, i) => (
        <div key={a.role}
              className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-4 py-3 mb-3 last:mb-0 shadow-sm"
              style={{ animation: `fadeUp .6s ${i * .12}s both` }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: a.color + "18", border: `1px solid ${a.color}44` }}>
            <Users2 className="w-4 h-4" style={{ color: a.color }} />
          </div>
          <div className="flex-1">
            <div className="text-xs text-slate-500">{a.role}</div>
            <div className="text-sm font-mono text-slate-800">{a.email}</div>
          </div>
          <div className="text-[10px] tracking-[2px] uppercase font-bold px-2 py-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 text-emerald-700">
            Acceso propio
          </div>
        </div>
      ))}
    </div>
  );
}


/* ═══════════════════════ FOOTER ═══════════════════════ */
function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white pt-14 pb-10" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-6 items-center">
        <div className="flex items-center gap-4">
          <img src="/buddy-avanza-icon.png" alt="BUDDY AVANZA" className="h-14 w-14 object-contain" />
          <div>
            <div className="text-[11px] tracking-[3px] uppercase text-slate-500 font-semibold">Descubre</div>
            <div className="mt-1 text-lg font-bold text-slate-900">BUDDY AVANZA</div>
            <div className="text-sm text-slate-600">Prepárate. Practica. Avanza.</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <Link to="/avanzo/comprar" data-testid="footer-cta"
                 className="inline-flex items-center px-5 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 transition-all shadow-md">
            Comenzar mi preparación <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
          <Link to="/" className="inline-flex items-center px-5 py-3 rounded-full text-sm font-semibold text-slate-800 border border-slate-200 hover:bg-slate-50 transition-all">
            Volver a QHUB.BUDDY
          </Link>
        </div>
      </div>
      <div className="mt-8 text-center text-[11px] text-slate-500">
        © 2026 QHUB.BUDDY · Todos los derechos reservados
      </div>
    </footer>
  );
}

/* ═══════════════════════ MOBILE STICKY CTA ═══════════════════════ */
function MobileStickyCTA({ price }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const trigger = document.querySelector('[data-testid="hero-section"]');
    if (!trigger || !("IntersectionObserver" in window)) {
      setVisible(true);
      return () => {};
    }
    const heroObs = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { rootMargin: "-200px 0px 0px 0px" }
    );
    heroObs.observe(trigger);
    const pricing = document.querySelector('[data-testid="pricing-section"]');
    let pricingObs = null;
    if (pricing) {
      pricingObs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setVisible(false); },
        { threshold: 0.2 }
      );
      pricingObs.observe(pricing);
    }
    return () => { heroObs.disconnect(); if (pricingObs) pricingObs.disconnect(); };
  }, []);

  return (
    <div
      data-testid="mobile-sticky-cta"
      aria-hidden={!visible}
      className={
        "md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 " +
        "bg-gradient-to-t from-white via-white/95 to-white/0 backdrop-blur-sm " +
        "transition-transform duration-300 " +
        (visible ? "translate-y-0" : "translate-y-full pointer-events-none")
      }
    >
      <Link
        to="/avanzo/comprar"
        data-testid="mobile-sticky-cta-link"
        className="group flex items-center justify-center gap-2 w-full py-4 px-5 rounded-full text-base font-bold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] shadow-2xl shadow-[#8b5cf6]/40 active:scale-95 transition-transform"
      >
        <Sparkles className="w-4 h-4" />
        Obtener 2 meses · US${price}
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
}



/* ═══════════════════════ AMBIENT LAYER (light) ═══════════════════════ */
function AmbientBackground() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-[-1] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[100vh] mesh-glow-light" />
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid-light" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#0f172a" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-light)" />
      </svg>
    </div>
  );
}
